import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  batchHistoryService,
  BatchHistoryItem,
  BatchHistoryParams,
} from "../services/batchHistoryService";
import { BatchStatus } from "../types/batchStatus";
import Header from "./Header";
import BackNav from "./BackNav";
import Loading from "./Loading";
import JapaneseDatePicker from "./JapaneseDatePicker";
import CustomSelect from "./CustomSelect";
import "./BatchHistoryList.css";

interface BatchHistoryListProps {
  user: any;
  onLogout: () => void;
}

interface PaginationInfo {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

const BatchHistoryList: React.FC<BatchHistoryListProps> = ({
  user,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [batchHistory, setBatchHistory] = useState<BatchHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    perPage: 25,
    total: 0,
    totalPages: 0,
  });
  // Filter states
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [status, setStatus] = useState<BatchStatus>(BatchStatus.ALL);

  // Track initial values to detect changes
  const [initialStartDate, setInitialStartDate] = useState<Date | null>(null);
  const [initialEndDate, setInitialEndDate] = useState<Date | null>(null);
  const [initialStatus, setInitialStatus] = useState<BatchStatus>(
    BatchStatus.ALL
  );

  // Ref to prevent duplicate API calls
  const isLoadingRef = useRef(false);

  // Expanded error message state
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());

  // Import button state
  const [importing, setImporting] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  // Helper functions to convert between Date objects and string formats
  const dateToString = (date: Date | null): string => {
    if (!date) return "";
    // Use local timezone to avoid date shifting
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // YYYY-MM-DD format
  };

  const stringToDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    // Parse date in local timezone to avoid timezone issues
    const [year, month, day] = dateString.split("-").map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return isNaN(date.getTime()) ? null : date;
  };

  // Initialize state from URL parameters on component mount
  useEffect(() => {
    const page = parseInt(searchParams.get("page") || "1");
    const startDateParam = searchParams.get("startDate") || "";
    const endDateParam = searchParams.get("endDate") || "";
    const statusParam =
      (searchParams.get("status") as BatchStatus) || BatchStatus.ALL;

    // Convert string dates to Date objects
    const startDateObj = stringToDate(startDateParam);
    const endDateObj = stringToDate(endDateParam);

    // Batch all state updates to prevent multiple re-renders
    setPagination(prev => ({
      ...prev,
      page: page,
    }));
    setStartDate(startDateObj);
    setEndDate(endDateObj);
    setStatus(statusParam);

    // Set initial values for comparison
    setInitialStartDate(startDateObj);
    setInitialEndDate(endDateObj);
    setInitialStatus(statusParam);

    loadBatchHistory(page, startDateParam, endDateParam, statusParam);
  }, [searchParams]);

  // Update URL parameters when filters or pagination change
  const updateURL = (
    page: number,
    startDate?: Date | null,
    endDate?: Date | null,
    status?: string
  ) => {
    const params = new URLSearchParams();
    if (page > 1) {
      params.set("page", page.toString());
    }
    if (startDate) {
      params.set("startDate", dateToString(startDate));
    }
    if (endDate) {
      params.set("endDate", dateToString(endDate));
    }
    if (status && status !== "ALL") {
      params.set("status", status);
    }
    setSearchParams(params);
  };

  const loadBatchHistory = async (
    page: number = 1,
    startDate?: string,
    endDate?: string,
    status?: BatchStatus
  ) => {
    // Prevent duplicate API calls
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      const params: BatchHistoryParams = {
        page,
        perPage: pagination.perPage,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        status: status || BatchStatus.ALL,
      };

      const response = await batchHistoryService.getBatchHistory(params);
      if (response.success) {
        setBatchHistory(response.data);
        setPagination({
          page: response.pagination.page,
          perPage: response.pagination.perPage,
          total: parseInt(response.pagination.total),
          totalPages: response.pagination.totalPages,
        });
      } else {
        setError(response.message || "連携ログの取得に失敗しました");
      }
    } catch (err: any) {
      setError(err.message || "連携ログの取得に失敗しました");
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Check if there are any changes in filters
  // const hasFilterChanges = () => {
  //   const startDateStr = dateToString(startDate);
  //   const endDateStr = dateToString(endDate);
  //   const initialStartDateStr = dateToString(initialStartDate);
  //   const initialEndDateStr = dateToString(initialEndDate);

  //   return startDateStr !== initialStartDateStr ||
  //          endDateStr !== initialEndDateStr ||
  //          status !== initialStatus;
  // };

  const handleSearch = () => {
    // Always allow search, even if no changes detected
    updateURL(1, startDate, endDate, status);
    loadBatchHistory(1, dateToString(startDate), dateToString(endDate), status);

    // Update initial values after successful search
    setInitialStartDate(startDate);
    setInitialEndDate(endDate);
    setInitialStatus(status);
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setStatus(BatchStatus.ALL);

    // Update initial values when clearing
    setInitialStartDate(null);
    setInitialEndDate(null);
    setInitialStatus(BatchStatus.ALL);

    updateURL(1, null, null, BatchStatus.ALL);
    loadBatchHistory(1, "", "", BatchStatus.ALL);
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);

    if (endDate && date && date > endDate) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);

    if (startDate && date && date < startDate) {
      setStartDate(null);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      // Use initial values instead of current filter values to maintain consistency
      updateURL(page, initialStartDate, initialEndDate, initialStatus);
      loadBatchHistory(
        page,
        dateToString(initialStartDate),
        dateToString(initialEndDate),
        initialStatus
      );
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, pagination.page - 3);
    const endPage = Math.min(pagination.totalPages, pagination.page + 3);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const formatDate = (dateString: string) => {
    return batchHistoryService.formatExecutionDate(dateString);
  };

  const getStatusDisplayText = (status: BatchStatus) => {
    return batchHistoryService.getStatusDisplayText(status);
  };

  const getStatusClass = (status: BatchStatus) => {
    return batchHistoryService.getStatusClass(status);
  };

  const formatDocumentStats = (stats: BatchHistoryItem["documentStats"]) => {
    return batchHistoryService.formatDocumentStats(stats);
  };

  // Helper functions for error message handling
  const isErrorMessageTooLong = (errorMessage: string): boolean => {
    return errorMessage.length > 100;
  };

  const truncateErrorMessage = (
    errorMessage: string,
    maxLength: number = 100
  ): string => {
    if (errorMessage.length <= maxLength) return errorMessage;
    return errorMessage.substring(0, maxLength) + "...";
  };

  const toggleErrorExpansion = (batchId: number) => {
    setExpandedErrors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };

  const renderDocumentStats = (
    stats: BatchHistoryItem["documentStats"],
    isOverallError: boolean
  ) => {
    const { total, success, failed } = formatDocumentStats(stats);

    const failedCount = failed ? failed : isOverallError ? total - success : 0;

    return (
      <div className="document-stats-container">
        <div className="stat-row total-row">
          <span className="stat-label">総処理件数:</span>
          <span className="stat-value total-value">{total}件</span>
        </div>
        <div className="stat-row success-row">
          <span className="stat-label">正常:</span>
          <span className="stat-value success-value">{success}件</span>
        </div>
        <div className="stat-row pending-row">
          <span className="stat-label">処理中:</span>
          <span className="stat-value pending-value">
            {total - success - failedCount}件
          </span>
        </div>
        <div className="stat-row error-row">
          <span className="stat-label">エラー:</span>
          <span className="stat-value error-value">{failedCount}件</span>
        </div>
      </div>
    );
  };

  const renderErrorMessage = (item: BatchHistoryItem) => {
    if (!item.errorMsg) {
      return (
        <div className="error-message-container no-error">
          <span className="error-text">-</span>
        </div>
      );
    }

    const isExpanded = expandedErrors.has(item.id);
    const isTooLong = isErrorMessageTooLong(item.errorMsg);

    return (
      <div className="error-message-container">
        <span className="error-text">
          {isExpanded
            ? item.errorMsg
            : isTooLong
              ? truncateErrorMessage(item.errorMsg)
              : item.errorMsg}
        </span>
        {isTooLong && (
          <button
            className="error-toggle-button"
            onClick={() => toggleErrorExpansion(item.id)}
            title={isExpanded ? "折りたたむ" : "展開"}
          >
            {isExpanded ? "▲" : "⋯"}
          </button>
        )}
      </div>
    );
  };

  const handleImport = async () => {
    if (importing) return;
    setImporting(true);
    try {
      const result = await batchHistoryService.triggerImport();
      if (result.success) {
        if (result.data && result.data.sentRecords > 0) {
          toast.success(
            result.message || `${result.data.sentRecords}件の申請書を取込キューに送信しました`
          );
          // Refresh the list to show the new batch execution
          await loadBatchHistory(pagination.page);
        } else {
          toast(result.message || "取込対象の申請書がありません", {
            icon: "ℹ️",
          });
        }
      } else {
        toast.error(result.message || "取込処理に失敗しました");
      }
    } catch (err: any) {
      toast.error(err.message || "取込処理に失敗しました");
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="batch-history-container">
        <Loading message="連携ログを読み込み中..." fullHeight={true} />
      </div>
    );
  }

  return (
    <div className="batch-history-container">
      {/* Header */}
      <Header user={user} onLogout={onLogout} />
      {/* Back Button (aligned with content) */}
      <BackNav label="ダッシュボードに戻る" to="/" />

      {/* Content */}
      <div className="batch-history-content">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-row">
            <h1 className="page-title">連携ログ一覧</h1>
            <button
              className="import-all-button"
              onClick={handleImport}
              disabled={importing}
            >
              {importing ? "取込中..." : "取込"}
            </button>
          </div>
          <p className="page-description">
            連携の実行履歴と処理件数を確認します
          </p>
        </div>

        {/* Separator */}
        <div className="separator"></div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-left">
            <div className="filter-group">
              <label className="filter-label">実行日</label>
              <div className="date-filters">
                <JapaneseDatePicker
                  selected={startDate}
                  onChange={handleStartDateChange}
                  placeholder="yyyy/mm/dd"
                  maxDate={endDate}
                  className="date-input"
                />
                <span className="date-separator">〜</span>
                <JapaneseDatePicker
                  selected={endDate}
                  onChange={handleEndDateChange}
                  placeholder="yyyy/mm/dd"
                  minDate={startDate}
                  className="date-input"
                />
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">実行結果</label>
              <CustomSelect
                value={status}
                onChange={(value: string) => setStatus(value as BatchStatus)}
                options={[
                  { value: BatchStatus.ALL, label: "すべて" },
                  { value: BatchStatus.SUCCESS, label: "成功" },
                  { value: BatchStatus.FAILED, label: "エラー" },
                  { value: BatchStatus.PENDING, label: "処理中" },
                ]}
                placeholder="実行結果を選択"
                className="status-select"
              />
            </div>

            <div className="filter-actions">
              <button className="search-button" onClick={handleSearch}>
                検索
              </button>
              {(startDate || endDate || status !== BatchStatus.ALL) && (
                <button
                  className="clear-search-button"
                  onClick={handleClearFilters}
                  title="フィルターをクリア"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="table-section">
          {error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button
                className="retry-button"
                onClick={() =>
                  loadBatchHistory(
                    pagination.page,
                    dateToString(startDate),
                    dateToString(endDate),
                    status
                  )
                }
              >
                再試行
              </button>
            </div>
          ) : batchHistory.length === 0 ? (
            <div className="empty-container">
              <p className="empty-message">データがありません</p>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="batch-history-table">
                  <thead>
                    <tr>
                      <th className="execution-date-column">実行日時</th>
                      <th className="execution-result-column">実行結果</th>
                      <th className="processed-items-column">処理件数</th>
                      <th className="error-summary-column">エラー概要</th>
                      <th className="details-column">詳細</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchHistory.map((item, index) => (
                      <tr
                        key={item.id}
                        className={`batch-history-row ${index % 2 === 0 ? "even" : "odd"}`}
                      >
                        <td className="execution-date-cell">
                          {formatDate(item.executionDate)}
                        </td>
                        <td className="execution-result-cell">
                          <span
                            className={`status-badge ${getStatusClass(item.status)}`}
                          >
                            {getStatusDisplayText(item.status)}
                          </span>
                        </td>
                        <td className="processed-items-cell">
                          {renderDocumentStats(
                            item.documentStats,
                            item.errorMsg && item.errorMsg.length > 0
                              ? true
                              : false
                          )}
                        </td>
                        <td className="error-summary-cell">
                          {renderErrorMessage(item)}
                        </td>
                        <td className="details-cell">
                          <button
                            className="details-button"
                            onClick={() => {
                              navigate(`/batch_history/${item.id}`);
                            }}
                            title="詳細"
                            disabled={!item.isDetailViewable}
                          >
                            詳細
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Pagination Section */}
        {!error && batchHistory.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-info">
              {(pagination.page - 1) * pagination.perPage + 1} -{" "}
              {Math.min(pagination.page * pagination.perPage, pagination.total)}{" "}
              / {pagination.total}
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
                title="最初のページ"
              >
                «
              </button>
              <button
                className="pagination-button"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                title="前のページ"
              >
                ‹
              </button>
              {getPageNumbers().map(page => (
                <button
                  key={page}
                  className={`pagination-button ${page === pagination.page ? "active" : ""}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className="pagination-button"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                title="次のページ"
              >
                ›
              </button>
              <button
                className="pagination-button"
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.page === pagination.totalPages}
                title="最後のページ"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchHistoryList;
